const meAutoScrollTouchCapable = Number(navigator.maxTouchPoints || 0) > 0;
const meAutoScrollDesktopHoverQuery = window.matchMedia?.(
  '(min-width: 1200px) and (hover: hover) and (pointer: fine)'
);

document.documentElement.toggleAttribute(
  'data-me-touch-capable',
  meAutoScrollTouchCapable
);

const shouldPauseMeAutoScrollForHover = () =>
  !meAutoScrollTouchCapable && Boolean(meAutoScrollDesktopHoverQuery?.matches);

const isMeAutoScrollHoverPointer = (event) =>
  shouldPauseMeAutoScrollForHover() &&
  (event.pointerType === 'mouse' ||
    (event.pointerType === 'pen' && event.buttons === 0));

const initElementAutoScroll = () => {
  const carousels = document.querySelectorAll('[data-me-auto-scroll]:not([data-me-auto-scroll-ready])');

  carousels.forEach((carousel) => {
    const scroller = carousel.querySelector('slideshow-slides');
    if (!scroller) return;

    carousel.dataset.meAutoScrollReady = 'true';

    const duration = Number(carousel.dataset.meAutoScrollSpeed || 24);
    const pixelsPerFrame = Math.max(0.35, scroller.scrollWidth / Math.max(duration * 60, 1));
    let pointerPaused = false;
    let focusPaused = false;

    carousel.addEventListener('pointerenter', (event) => {
      pointerPaused = isMeAutoScrollHoverPointer(event);
    });
    carousel.addEventListener('pointerleave', () => {
      pointerPaused = false;
    });
    carousel.addEventListener('pointercancel', () => {
      pointerPaused = false;
    });
    carousel.addEventListener('focusin', () => {
      focusPaused = true;
    });
    carousel.addEventListener('focusout', () => {
      focusPaused = false;
    });

    const tick = () => {
      const hoverPaused = pointerPaused && shouldPauseMeAutoScrollForHover();

      if (!hoverPaused && !focusPaused && scroller.scrollWidth > scroller.clientWidth) {
        const maxScroll = scroller.scrollWidth - scroller.clientWidth - 2;
        scroller.scrollLeft = scroller.scrollLeft >= maxScroll ? 0 : scroller.scrollLeft + pixelsPerFrame;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
};

const initHomeSwimAutoScroll = () => {
  document.querySelectorAll('[data-me-home-swim-auto-scroll]:not([data-me-home-swim-auto-ready])').forEach((carousel) => {
    const track = carousel.querySelector('.me-home-swim__track');
    const viewport = carousel.querySelector('.me-home-swim__viewport');
    if (!track) return;

    const originalCards = Array.from(track.children);
    if (originalCards.length < 2) return;

    carousel.dataset.meHomeSwimAutoReady = 'true';

    const cloneOriginalCards = () => {
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('data-me-tap-active');
        clone.removeAttribute('data-me-pointer-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((interactive) => {
          interactive.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });
    };

    const getLoopWidth = () => {
      const firstClone = track.children[originalCards.length];
      return firstClone ? firstClone.offsetLeft : track.scrollWidth / 2;
    };

    const fillWideViewports = () => {
      const viewportWidth = viewport?.clientWidth || carousel.clientWidth || 0;
      let loopWidth = getLoopWidth();
      let guard = 0;

      while (loopWidth > 0 && track.scrollWidth < viewportWidth + loopWidth * 2 && guard < 8) {
        cloneOriginalCards();
        loopWidth = getLoopWidth();
        guard += 1;
      }
    };

    cloneOriginalCards();
    fillWideViewports();

    const duration = Number(carousel.dataset.meHomeSwimSpeed || 30);
    const previous = carousel.querySelector('[data-me-home-swim-prev]');
    const next = carousel.querySelector('[data-me-home-swim-next]');
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const root = document.documentElement;
    let pointerPaused = false;
    let focusPaused = false;
    let manualPauseUntil = 0;
    let transitionTimer;
    let offset = 0;
    let lastTimestamp;

    const getStep = () => {
      const card = track.querySelector('.me-home-swim-card');
      if (!card) return carousel.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const syncActiveCard = () => {
      const bounds = (viewport || carousel).getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      let closestCard;
      let closestDistance = Infinity;

      track.querySelectorAll('.me-home-swim-card').forEach((card) => {
        const cardBounds = card.getBoundingClientRect();
        if (!cardBounds.width) return;

        const distance = Math.abs(cardBounds.left + cardBounds.width / 2 - center);
        if (distance < closestDistance) {
          closestCard = card;
          closestDistance = distance;
        }
      });

      track.querySelectorAll('.me-home-swim-card--active').forEach((card) => {
        if (card !== closestCard) card.classList.remove('me-home-swim-card--active');
      });

      closestCard?.classList.add('me-home-swim-card--active');
    };

    const applyOffset = ({ animated = false, normalize = true } = {}) => {
      const loopWidth = getLoopWidth();
      if (!loopWidth) return;

      if (normalize) {
        while (offset >= loopWidth) offset -= loopWidth;
        while (offset < 0) offset += loopWidth;
      }

      window.clearTimeout(transitionTimer);
      track.style.transition = animated ? 'transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)' : '';
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (animated) {
        transitionTimer = window.setTimeout(() => {
          track.style.transition = '';
          applyOffset();
        }, 430);
      } else {
        window.requestAnimationFrame(syncActiveCard);
      }
    };

    const moveByStep = (direction) => {
      const step = getStep();
      const loopWidth = getLoopWidth();
      if (!step || !loopWidth) return;

      manualPauseUntil = performance.now() + 1800;

      if (direction < 0 && offset <= 1) {
        offset = loopWidth;
        applyOffset({ normalize: false });
        track.getBoundingClientRect();
      }

      offset += step * direction;
      applyOffset({ animated: !reducedMotion?.matches, normalize: direction < 0 });
    };

    track.addEventListener('pointerover', (event) => {
      if (!isMeAutoScrollHoverPointer(event)) {
        pointerPaused = false;
        return;
      }
      pointerPaused = Boolean(event.target instanceof Element && event.target.closest('.me-home-swim-card'));
    });

    track.addEventListener('pointerout', (event) => {
      if (!isMeAutoScrollHoverPointer(event)) {
        if (!shouldPauseMeAutoScrollForHover()) pointerPaused = false;
        return;
      }

      const relatedCard =
        event.relatedTarget instanceof Element
          ? event.relatedTarget.closest('.me-home-swim-card')
          : null;

      if (!relatedCard || !track.contains(relatedCard)) pointerPaused = false;
    });

    track.addEventListener('pointerleave', () => {
      pointerPaused = false;
    });

    track.addEventListener('focusin', (event) => {
      focusPaused = Boolean(
        event.target instanceof Element && event.target.closest('.me-home-swim-card')
      );
    });

    track.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        focusPaused = Boolean(
          document.activeElement instanceof Element &&
            track.contains(document.activeElement) &&
            document.activeElement.closest('.me-home-swim-card')
        );
      });
    });

    const resetTransientCarouselPause = () => {
      pointerPaused = false;
      focusPaused = false;
      lastTimestamp = undefined;
    };

    track.addEventListener('pointercancel', () => {
      pointerPaused = false;
      lastTimestamp = undefined;
    });

    window.addEventListener('blur', resetTransientCarouselPause);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') resetTransientCarouselPause();
    });

    previous?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveByStep(-1);
    });

    next?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveByStep(1);
    });

    window.addEventListener(
      'resize',
      () => {
        fillWideViewports();
        if (!shouldPauseMeAutoScrollForHover()) pointerPaused = false;
      },
      { passive: true }
    );
    syncActiveCard();

    const tick = (timestamp) => {
      const loopWidth = getLoopWidth();
      const delta = lastTimestamp == null ? 0 : timestamp - lastTimestamp;

      lastTimestamp = timestamp;

      if (
        !reducedMotion?.matches &&
        !(pointerPaused && shouldPauseMeAutoScrollForHover()) &&
        !focusPaused &&
        !root.hasAttribute('data-me-product-preview-suspended') &&
        !carousel.hasAttribute('data-me-home-swim-tap-active') &&
        performance.now() > manualPauseUntil &&
        loopWidth > 0
      ) {
        offset += (loopWidth / Math.max(duration, 1)) * (delta / 1000);
        applyOffset();
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
};

const initProductCardTapReveal = () => {
  const root = document.documentElement;
  if (root.dataset.meTapRevealReady === 'true') return;

  const cardSelector =
    '.me-home-swim-card, .me-collection-card, .product-recommendations product-card';
  const activeCardSelector =
    ".me-home-swim-card[data-me-tap-active='true'], .me-collection-card[data-me-tap-active='true'], .product-recommendations product-card[data-me-tap-active='true']";
  const activationMemoryDuration = 3000;
  const syntheticMouseSuppressionDuration = 1400;
  const touchPreviewIdleDuration = 4000;
  let pendingActivation = null;
  let lastTouchPointerAt = Number.NEGATIVE_INFINITY;
  let finePreviewCard = null;
  let penHoverCard = null;
  let penHoverSeen = false;
  let touchPreviewCard = null;
  let touchPreviewTimer;

  const getCard = (target) => (target instanceof Element ? target.closest(cardSelector) : null);
  const getFinePreviewCard = (target) => {
    if (!(target instanceof Element)) return null;

    const card = target.closest('.me-home-swim-card, .me-collection-card');
    if (!card) return null;

    if (card.matches('.me-home-swim-card')) return card;

    return card.closest('[data-me-untamed-product-listing], .product-recommendations')
      ? card
      : null;
  };

  const getCardLink = (card, target) => {
    if (!card || !(target instanceof Element)) return null;

    const targetedLink = target.closest('a[href]');
    if (targetedLink && card.contains(targetedLink)) return targetedLink;

    return card.matches('a[href]') ? card : null;
  };

  const prepareProductViewTransition = (card) => {
    if (!card || !document.querySelector("main[data-product-transition='true']")) return;

    const media = card.querySelector('.me-home-swim-card__media, .me-collection-card__media');
    if (!(media instanceof HTMLElement)) return;

    document.querySelectorAll('[data-me-custom-product-transition-source]').forEach((source) => {
      if (source === media) return;
      source.removeAttribute('data-view-transition-triggered');
      source.removeAttribute('data-view-transition-type');
      source.removeAttribute('data-me-custom-product-transition-source');
    });

    media.dataset.viewTransitionType = 'product-image-transition';
    media.dataset.viewTransitionTriggered = 'true';
    media.dataset.meCustomProductTransitionSource = 'true';
  };

  const syncHomeCarouselState = () => {
    document.querySelectorAll('[data-me-home-swim-carousel]').forEach((carousel) => {
      const hasRevealedCard = Boolean(
        carousel.querySelector(".me-home-swim-card[data-me-tap-active='true']")
      );
      carousel.toggleAttribute('data-me-home-swim-tap-active', hasRevealedCard);
    });
  };

  const clearRevealedCards = (exceptCard = null) => {
    let changed = false;

    if (touchPreviewCard && touchPreviewCard !== exceptCard) {
      clearTouchPreviewTimer();
    }

    document.querySelectorAll(activeCardSelector).forEach((card) => {
      if (card === exceptCard) return;

      card.removeAttribute('data-me-tap-active');
      changed = true;

      const focusedElement = document.activeElement;
      if (focusedElement instanceof HTMLElement && card.contains(focusedElement)) {
        focusedElement.blur();
      }
    });

    if (changed) syncHomeCarouselState();
  };

  const setFinePreviewCard = (card = null) => {
    const nextCard =
      card?.matches('.me-home-swim-card') ||
      (card?.matches('.me-collection-card') &&
        card.closest('[data-me-untamed-product-listing], .product-recommendations'))
        ? card
        : null;
    if (
      finePreviewCard === nextCard &&
      (!nextCard || nextCard.dataset.mePointerActive === 'true')
    ) {
      return;
    }

    finePreviewCard?.removeAttribute('data-me-pointer-active');
    finePreviewCard = nextCard;
    finePreviewCard?.setAttribute('data-me-pointer-active', 'true');
  };

  const clearFinePreviewCard = () => {
    setFinePreviewCard();
  };

  const setPenHoverState = (target) => {
    const nextCard = getCard(target);
    penHoverCard = nextCard;
    penHoverSeen = true;
  };

  const clearPenHoverState = () => {
    penHoverSeen = false;
    penHoverCard = null;
  };

  const setPendingActivation = (target, mode) => {
    pendingActivation = {
      card: getCard(target),
      mode,
      recordedAt: performance.now(),
    };
  };

  const clearPendingActivation = () => {
    pendingActivation = null;
  };

  const consumePendingActivation = (card) => {
    const activation = pendingActivation;
    clearPendingActivation();

    if (
      !activation ||
      activation.card !== card ||
      performance.now() - activation.recordedAt > activationMemoryDuration
    ) {
      return null;
    }

    return activation.mode;
  };

  const clearTouchPreviewTimer = () => {
    window.clearTimeout(touchPreviewTimer);
    touchPreviewTimer = undefined;
    touchPreviewCard = null;
  };

  const scheduleTouchPreviewExpiry = (card) => {
    clearTouchPreviewTimer();
    touchPreviewCard = card;
    touchPreviewTimer = window.setTimeout(() => {
      const expiredCard = touchPreviewCard;
      touchPreviewTimer = undefined;
      touchPreviewCard = null;

      if (expiredCard?.dataset.meTapActive === 'true') {
        clearRevealedCards();
      }
    }, touchPreviewIdleDuration);
  };

  const blurFocusedPreview = () => {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && getCard(activeElement)) {
      activeElement.blur();
    }
  };

  const resetProductPreviewState = () => {
    blurFocusedPreview();
    clearPendingActivation();
    clearTouchPreviewTimer();
    delete root.dataset.meFinePointer;
    clearPenHoverState();
    lastTouchPointerAt = Number.NEGATIVE_INFINITY;
    clearFinePreviewCard();
    clearRevealedCards();
  };

  const activateFinePointer = (card = null) => {
    root.dataset.meFinePointer = 'true';
    clearRevealedCards(card);
  };

  if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
    root.dataset.meFinePointer = 'true';
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      const isHoveringPen = event.pointerType === 'pen' && event.buttons === 0;
      const isMouse =
        event.pointerType === 'mouse' &&
        performance.now() - lastTouchPointerAt > syntheticMouseSuppressionDuration;

      if (!isMouse && !isHoveringPen) return;

      if (isHoveringPen) {
        setPenHoverState(event.target);
      }
      activateFinePointer(getCard(event.target));
      setFinePreviewCard(getFinePreviewCard(event.target));
    },
    true
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType === 'pen' && event.buttons === 0) {
        setPenHoverState(event.target);
        activateFinePointer(getCard(event.target));
        setFinePreviewCard(getFinePreviewCard(event.target));
        return;
      }

      if (
        event.pointerType === 'mouse' &&
        performance.now() - lastTouchPointerAt > syntheticMouseSuppressionDuration
      ) {
        activateFinePointer(getCard(event.target));
        setFinePreviewCard(getFinePreviewCard(event.target));
      }
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

      if (event.pointerType === 'pen') {
        const fromProductCard = getCard(event.target);
        if (fromProductCard && fromProductCard === penHoverCard) {
          const relatedCard = getCard(event.relatedTarget);
          if (relatedCard) {
            setPenHoverState(event.relatedTarget);
          } else {
            clearPenHoverState();
          }
        }
      }

      const fromCard = getFinePreviewCard(event.target);
      if (!fromCard || fromCard !== finePreviewCard) return;

      const relatedCard = getFinePreviewCard(event.relatedTarget);
      if (relatedCard !== fromCard) setFinePreviewCard(relatedCard);
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const pointerCard = getCard(event.target);

      if (event.pointerType === 'mouse') {
        setPendingActivation(event.target, 'fine');
        activateFinePointer(pointerCard);
        setFinePreviewCard(getFinePreviewCard(event.target));
        return;
      }

      if (
        event.pointerType === 'pen' &&
        (root.dataset.meFinePointer === 'true' ||
          (penHoverSeen && penHoverCard === pointerCard))
      ) {
        setPendingActivation(event.target, 'fine');
        activateFinePointer(pointerCard);
        setFinePreviewCard(getFinePreviewCard(event.target));
        return;
      }

      if (
        (event.pointerType !== 'touch' && event.pointerType !== 'pen') ||
        !event.isPrimary ||
        event.button !== 0
      ) {
        return;
      }

      delete root.dataset.meFinePointer;
      clearPenHoverState();
      clearFinePreviewCard();
      lastTouchPointerAt = performance.now();
      setPendingActivation(event.target, 'touch');

      const touchedCard = getCard(event.target);
      if (touchedCard?.dataset.meTapActive === 'true') {
        scheduleTouchPreviewExpiry(touchedCard);
      }
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    resetProductPreviewState,
    true
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (event.changedTouches.length !== 1) return;
      if (pendingActivation?.mode === 'fine') return;

      delete root.dataset.meFinePointer;
      clearPenHoverState();
      clearFinePreviewCard();
      lastTouchPointerAt = performance.now();
      setPendingActivation(event.target, 'touch');
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      const card = getCard(event.target);
      const activationMode = consumePendingActivation(card);
      const clickPointerType = typeof event.pointerType === 'string' ? event.pointerType : '';
      const isTouchActivation =
        activationMode === 'touch' ||
        (activationMode == null &&
          (clickPointerType === 'touch' ||
            (clickPointerType === 'pen' && root.dataset.meFinePointer !== 'true')));

      if (
        event.defaultPrevented ||
        event.detail === 0 ||
        (event.clientX === 0 && event.clientY === 0) ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (!card) {
        clearRevealedCards();
        return;
      }

      const link = getCardLink(card, event.target);
      if (!link || (event.target !== link && !link.contains(event.target))) {
        clearRevealedCards();
        return;
      }

      if (!isTouchActivation) {
        if (touchPreviewCard === card) clearTouchPreviewTimer();
        prepareProductViewTransition(card);
        return;
      }

      if (card.dataset.meTapActive === 'true') {
        if (touchPreviewCard === card) clearTouchPreviewTimer();
        prepareProductViewTransition(card);
        return;
      }

      event.preventDefault();
      clearRevealedCards(card);
      card.dataset.meTapActive = 'true';
      syncHomeCarouselState();
      scheduleTouchPreviewExpiry(card);
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') resetProductPreviewState();
  });

  document.addEventListener('focusin', (event) => {
    const focusedCard = getCard(event.target);
    if (focusedCard?.dataset.meTapActive !== 'true') clearRevealedCards();
  });

  window.addEventListener('blur', resetProductPreviewState);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') resetProductPreviewState();
  });

  const isPreviewSuspended = () =>
    root.dataset.meAccountOpen === 'true' ||
    Boolean(
      document.querySelector(
        "#Details-menu-drawer-container[open], .cart-drawer__dialog[open]"
      )
    );

  const syncPreviewSuspension = () => {
    const suspended = isPreviewSuspended();
    root.toggleAttribute('data-me-product-preview-suspended', suspended);

    if (suspended) {
      resetProductPreviewState();
    }
  };

  const previewSuspensionObserver = new MutationObserver(syncPreviewSuspension);
  previewSuspensionObserver.observe(root, {
    attributes: true,
    attributeFilter: ['data-me-account-open', 'open'],
    childList: true,
    subtree: true,
  });
  syncPreviewSuspension();

  root.dataset.meTapRevealReady = 'true';
};

const initElementCursorPrices = () => {
  const cards = document.querySelectorAll(
    '.product-card:not([data-me-cursor-ready]), .me-untamed-card:not([data-me-cursor-ready])'
  );

  cards.forEach((card) => {
    card.dataset.meCursorReady = 'true';
    let frame = 0;
    let nextX = 0;
    let nextY = 0;
    let bounds = null;
    let label = null;
    let labelWidth = 0;

    const getLabel = () =>
      card.querySelector('.me-product-media-meta, .me-product-hover-overlay__inner');

    const commitCursor = () => {
      frame = 0;
      if (!bounds || !label) return;

      const xInset = Math.min(Math.max(labelWidth / 2, 72), bounds.width / 2);
      const minX = Math.min(xInset, bounds.width / 2);
      const maxX = Math.max(minX, bounds.width - minX);
      const minY = Math.min(68, bounds.height / 2);
      const maxY = Math.max(minY, bounds.height - 32);
      const x = Math.min(Math.max(nextX, minX), maxX);
      const y = Math.min(Math.max(nextY, minY), maxY);

      label.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -116%)`;
    };

    const updateCursor = (event) => {
      bounds = bounds || card.getBoundingClientRect();
      nextX = event.clientX - bounds.left;
      nextY = event.clientY - bounds.top;

      if (!frame) {
        frame = requestAnimationFrame(commitCursor);
      }
    };

    card.addEventListener('pointerenter', (event) => {
      label = getLabel();
      bounds = card.getBoundingClientRect();
      labelWidth = label?.offsetWidth || 0;
      updateCursor(event);
    });

    card.addEventListener('pointermove', updateCursor);

    card.addEventListener('pointerleave', () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      bounds = null;
      label?.style.removeProperty('transform');
      label = null;
      labelWidth = 0;
    });
  });
};

const initUntamedCarousel = () => {
  document.querySelectorAll('[data-me-untamed-carousel]:not([data-me-untamed-ready])').forEach((carousel) => {
    const track = carousel.querySelector('.me-untamed-showcase__track');
    const previous = carousel.querySelector('[data-me-untamed-prev]');
    const next = carousel.querySelector('[data-me-untamed-next]');
    if (!track || !previous || !next) return;

    carousel.dataset.meUntamedReady = 'true';

    const getStep = () => {
      const card = track.querySelector('.me-untamed-card');
      if (!card) return track.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    previous.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 2;
      if (track.scrollLeft >= maxScroll) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      }
    });
  });
};

const initAccountBackdrop = () => {
  const root = document.documentElement;

  document.querySelectorAll('shopify-account:not([data-me-backdrop-bound])').forEach((account) => {
    account.dataset.meBackdropBound = 'true';
    account.addEventListener('open', () => {
      root.dataset.meAccountOpen = 'true';
    });
    account.addEventListener('close', () => {
      delete root.dataset.meAccountOpen;
    });
  });
};

let elementInstafeedObserver;
let elementInstafeedContentObserver;
let elementInstafeedObservedFeed;
let elementInstafeedRetryTimer;
let elementInstafeedSyncTimer;
let elementInstafeedVideoObserver;
let elementInstafeedTrackAnimationFrame;
let elementInstafeedVisibilityHandler;
let elementInstafeedPageShowHandler;
let elementInstafeedReducedMotionQuery;
let elementInstafeedCandidateFingerprint = '';
let elementInstafeedCandidatePasses = 0;
let elementInstafeedCandidateSince = 0;
let elementInstafeedRecoveryHandler;
let elementInstafeedSectionObserver;

const elementInstafeedMediaContainerSelector = [
  '.ifs-slide',
  '.instafeed-container',
  '.instafeed-new-layout-item',
  '.instafeed-item',
  '[data-instafeed-item]',
  '[data-instafeed-video-src]',
  '[data-video-src]',
  '[data-video-url]',
].join(', ');

const isElementInstafeedPlaceholderUrl = (url = '') => {
  const normalizedUrl = url.toLowerCase();
  return (
    !normalizedUrl ||
    normalizedUrl.includes('/assets/img/placeholder') ||
    normalizedUrl.includes('/assets/img/instagram-icon') ||
    normalizedUrl.includes('/assets/img/video-icon') ||
    normalizedUrl.includes('transparent.gif')
  );
};

const getElementInstafeedSrcsetUrl = (srcset = '') =>
  srcset
    .split(',')
    .at(-1)
    ?.trim()
    .split(/\s+/)[0] || '';

const isElementInstafeedMediaImage = (image) => {
  if (!(image instanceof HTMLImageElement)) return false;

  const postLink = image.closest('a[href*="instagram.com/"]');
  const feedRoot = image.closest('#insta-feed, #instafeed, .instafeed-shopify');
  const linkedFeedMedia = Boolean(feedRoot && image.closest('a[href]'));
  return Boolean(
    image.closest(elementInstafeedMediaContainerSelector) ||
      postLink ||
      linkedFeedMedia ||
      image.hasAttribute('data-instafeed-src') ||
      image.hasAttribute('data-instafeed-video-src') ||
      image.hasAttribute('data-video-src') ||
      image.hasAttribute('data-video-url') ||
      image.hasAttribute('data-media-id') ||
      image.hasAttribute('data-post-id')
  );
};

const primeElementInstafeedMedia = (feed) => {
  if (!feed) return;

  [...feed.querySelectorAll('img')]
    .filter(isElementInstafeedMediaImage)
    .forEach((image, index) => {
      const lazySource = [
        image.dataset.instafeedSrc,
        image.dataset.src,
        image.dataset.lazySrc,
        image.dataset.original,
        image.dataset.originalSrc,
        getElementInstafeedSrcsetUrl(
          image.dataset.srcset || image.getAttribute('data-srcset') || ''
        ),
      ].find((url) => url && !url.startsWith('data:') && !url.startsWith('blob:'));
      const currentSource = image.currentSrc || image.getAttribute('src') || '';
      const lazySrcset = image.dataset.srcset || image.getAttribute('data-srcset');

      image.loading = index < 5 ? 'eager' : 'lazy';
      image.decoding = 'async';
      if ('fetchPriority' in image) {
        image.fetchPriority = index < 5 ? 'high' : 'auto';
      }

      if (lazySrcset && !image.getAttribute('srcset')) {
        image.setAttribute('srcset', lazySrcset);
      }
      if (lazySource && isElementInstafeedPlaceholderUrl(currentSource)) {
        image.src = lazySource;
      }
    });
};

const clearElementInstafeedRuntimeListeners = () => {
  window.cancelAnimationFrame(elementInstafeedTrackAnimationFrame);
  elementInstafeedTrackAnimationFrame = undefined;

  if (elementInstafeedVisibilityHandler) {
    document.removeEventListener('visibilitychange', elementInstafeedVisibilityHandler);
    if (elementInstafeedReducedMotionQuery?.removeEventListener) {
      elementInstafeedReducedMotionQuery.removeEventListener(
        'change',
        elementInstafeedVisibilityHandler
      );
    } else {
      elementInstafeedReducedMotionQuery?.removeListener?.(
        elementInstafeedVisibilityHandler
      );
    }
  }
  if (elementInstafeedPageShowHandler) {
    window.removeEventListener('pageshow', elementInstafeedPageShowHandler);
  }

  elementInstafeedVisibilityHandler = undefined;
  elementInstafeedPageShowHandler = undefined;
  elementInstafeedReducedMotionQuery = undefined;
};

const isUsableElementInstafeedRecord = (record) =>
  Boolean(
    record &&
      !record.isPlaceholder &&
      (record.hasRealSource || (record.isVideo && record.videoUrl))
  );

const getElementInstafeedSnapshot = (feed) => {
  const getSrcsetUrl = (srcset) => getElementInstafeedSrcsetUrl(srcset || '');
  const getBackgroundUrl = (element) => {
    if (!element) return '';
    const backgroundImage = element.style.backgroundImage || getComputedStyle(element).backgroundImage;
    return backgroundImage?.match(/^url\(["']?(.*?)["']?\)$/)?.[1] || '';
  };
  const isUsableUrl = (url) => url && !url.startsWith('data:') && !url.startsWith('blob:');
  const getMediaContainer = (image) =>
    image.closest(elementInstafeedMediaContainerSelector) ||
    image.closest('a[href]') ||
    image.parentElement;
  const getSourceUrl = (image) => {
    const container = getMediaContainer(image);
    const candidates = [
      image.dataset.instafeedSrc,
      image.dataset.src,
      image.dataset.lazySrc,
      image.dataset.original,
      getSrcsetUrl(image.dataset.srcset || image.getAttribute('data-srcset')),
      getSrcsetUrl(image.getAttribute('srcset')),
      getBackgroundUrl(image),
      getBackgroundUrl(container),
      image.currentSrc,
      image.getAttribute('src'),
    ];

    return candidates.find(isUsableUrl) || '';
  };
  const getVideoUrl = (image) => {
    const container = getMediaContainer(image);
    const link = image.closest('a');
    const video = container?.querySelector('video');
    const source = video?.querySelector('source');
    const roots = [image, link, container, video, source].filter(Boolean);
    const videoAttributeNames = [
      'data-instafeed-video-src',
      'data-video-src',
      'data-video-url',
      'data-video',
    ];

    const candidates = roots.flatMap((root) => [
      ...videoAttributeNames.map((attribute) => root.getAttribute?.(attribute)),
      ...(root instanceof HTMLVideoElement || root instanceof HTMLSourceElement
        ? [
            root.getAttribute?.('data-instafeed-src'),
            root.getAttribute?.('data-src'),
            root.currentSrc,
            root.getAttribute?.('src'),
          ]
        : []),
    ]);

    return (
      candidates.find(
        (url) => {
          const normalizedUrl = url?.toLowerCase() || '';
          return (
            isUsableUrl(url) &&
            !normalizedUrl.includes('/assets/img/video-icon') &&
            !normalizedUrl.includes('/assets/img/placeholder') &&
            !normalizedUrl.includes('/assets/img/instagram-icon')
          );
        }
      ) || ''
    );
  };
  const createPlaceholder = () => {
    const placeholder = document.createElement('div');
    const mark = document.createElement('span');
    const label = document.createElement('span');

    placeholder.className = 'me-social-scroll__placeholder';
    mark.className = 'me-social-scroll__placeholder-mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = 'ME';
    label.className = 'me-social-scroll__placeholder-label';
    label.textContent = 'Coming soon';
    placeholder.append(mark, label);

    return placeholder;
  };

  const recordsByPost = new Map();
  const sourceImages = [...feed.querySelectorAll('img')].filter(
    isElementInstafeedMediaImage
  );
  const getPostPermalinkKey = (href) => {
    if (!href) return '';

    try {
      const url = new URL(href, window.location.href);
      const hostname = url.hostname.toLowerCase();
      if (hostname !== 'instagram.com' && !hostname.endsWith('.instagram.com')) {
        return '';
      }
      const match = url.pathname.match(/^\/(p|reel|tv)\/([^/]+)/i);
      return match
        ? `instagram.com/${match[1].toLowerCase()}/${match[2]}`
        : '';
    } catch {
      return '';
    }
  };
  sourceImages.forEach((sourceImage) => {
    const sourceUrl = getSourceUrl(sourceImage);
    if (!sourceUrl) return;

    const sourceContainer = getMediaContainer(sourceImage);
    const sourceLink = sourceImage.closest('a');
    const permalinkKey = getPostPermalinkKey(sourceLink?.href);
    const explicitMediaKey = [
      sourceImage.dataset.mediaId,
      sourceImage.dataset.postId,
      sourceContainer?.dataset?.mediaId,
      sourceContainer?.dataset?.postId,
      sourceContainer?.dataset?.id,
    ].find(Boolean);

    const normalizedSourceUrl = sourceUrl.toLowerCase();
    const isPlaceholder =
      normalizedSourceUrl.includes('/assets/img/placeholder') ||
      normalizedSourceUrl.includes('/assets/img/instagram-icon');
    const isVideoIcon = normalizedSourceUrl.includes('/assets/img/video-icon');
    const hasVideoData = Boolean(
      sourceContainer?.matches(
        '[data-instafeed-video-src], [data-video-src], [data-video-url]'
      )
    );
    const isVideo =
      isVideoIcon || hasVideoData || Boolean(sourceContainer?.querySelector('video'));
    const videoUrl = isVideo ? getVideoUrl(sourceImage) : '';
    const postKey = permalinkKey ||
      (explicitMediaKey ? `media:${explicitMediaKey}` : `source:${sourceUrl}`);
    const hasRealSource = !isPlaceholder && !isVideoIcon;
    const record = {
      key: postKey,
      sourceUrl,
      videoUrl,
      hasRealSource,
      isPlaceholder,
      isVideo,
      isInstagramPost: Boolean(permalinkKey),
      href: sourceLink?.href || '',
      target: sourceLink?.target || '_blank',
      alt: sourceImage.alt || 'Missing Element on Instagram',
    };
    const existingRecord = recordsByPost.get(postKey);

    if (!existingRecord) {
      recordsByPost.set(postKey, record);
      return;
    }

    const preferredSource = existingRecord.hasRealSource
      ? existingRecord
      : record.hasRealSource
        ? record
        : existingRecord.sourceUrl
          ? existingRecord
          : record;
    const mergedHasRealSource =
      existingRecord.hasRealSource || record.hasRealSource;
    const mergedVideoUrl = record.videoUrl || existingRecord.videoUrl;

    recordsByPost.set(postKey, {
      ...existingRecord,
      sourceUrl: preferredSource.sourceUrl,
      videoUrl: mergedVideoUrl,
      hasRealSource: mergedHasRealSource,
      isPlaceholder:
        existingRecord.isPlaceholder &&
        record.isPlaceholder &&
        !mergedHasRealSource &&
        !mergedVideoUrl,
      isVideo: existingRecord.isVideo || record.isVideo,
      isInstagramPost:
        existingRecord.isInstagramPost || record.isInstagramPost,
      href: existingRecord.href || record.href,
      target: existingRecord.href ? existingRecord.target : record.target,
      alt: preferredSource.alt,
    });
  });

  const records = [...recordsByPost.values()];
  const fingerprint = records
    .map(
      ({ key, sourceUrl, videoUrl, href, hasRealSource, isPlaceholder, isVideo, isInstagramPost }) =>
        `${key}|${sourceUrl}|${videoUrl}|${href}|${hasRealSource}|${isPlaceholder}|${isVideo}|${isInstagramPost}`
    )
    .join('::');

  const liveMediaCount = records.filter(isUsableElementInstafeedRecord).length;

  return {
    records,
    fingerprint,
    liveMediaCount,
    createPlaceholder,
  };
};

const hydrateElementInstafeedTrack = (
  section,
  snapshot,
  { finalize = false } = {}
) => {
  const track = section.querySelector('[data-me-instafeed-track]');
  const viewport = section.querySelector('.me-social-scroll__viewport');

  if (!track || !snapshot?.records.length) return false;

  const declaredMinimumSlotCount = Number(
    track.dataset.meInstafeedMinimumSlotCount
  );
  const minimumSlotCount = Math.max(
    Number.isFinite(declaredMinimumSlotCount) ? declaredMinimumSlotCount : 0,
    5
  );
  const usableRecords = snapshot.records.filter(isUsableElementInstafeedRecord);
  const slotCount = Math.max(usableRecords.length, minimumSlotCount);
  const displayRecords = usableRecords;
  const displaySlots = Array.from(
    { length: slotCount },
    (_, index) => displayRecords[index] || null
  );
  const fragment = document.createDocumentFragment();

  for (let repeat = 0; repeat < 3; repeat += 1) {
    displaySlots.forEach((record, index) => {
      const item = document.createElement('div');
      const appendImage = () => {
        const image = document.createElement('img');

        image.className = 'me-social-scroll__live-image';
        image.src = record.sourceUrl;
        image.alt = record.alt;
        image.loading = repeat === 0 && index < 5 ? 'eager' : 'lazy';
        image.decoding = 'async';
        if ('fetchPriority' in image) {
          image.fetchPriority = repeat === 0 && index < 5 ? 'high' : 'auto';
        }
        image.addEventListener(
          'error',
          () => {
            item.replaceChildren(snapshot.createPlaceholder());
          },
          { once: true }
        );
        item.appendChild(image);
      };

      item.className = 'me-social-scroll__item me-social-scroll__item--live';
      if (repeat > 0) {
        item.setAttribute('aria-hidden', 'true');
      }

      if (!record || record.isPlaceholder) {
        item.appendChild(snapshot.createPlaceholder());
      } else if (record.isVideo && record.videoUrl) {
        const video = document.createElement('video');

        video.className = 'me-social-scroll__live-video';
        video.src = record.videoUrl;
        if (record.hasRealSource) video.poster = record.sourceUrl;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.addEventListener(
          'error',
          () => {
            item.replaceChildren(snapshot.createPlaceholder());
          },
          { once: true }
        );
        item.appendChild(video);
      } else if (record.hasRealSource) {
        appendImage();
      } else {
        item.appendChild(snapshot.createPlaceholder());
      }
      fragment.appendChild(item);
    });
  }

  track.style.animation = 'none';
  track.replaceChildren(fragment);
  track.dataset.meInstafeedSource = snapshot.fingerprint;

  if (finalize) {
    section.dataset.meInstafeedReady = 'true';
  } else {
    delete section.dataset.meInstafeedReady;
  }
  section.dataset.meInstafeedLoadedCount = String(usableRecords.length);
  track.dataset.meInstafeedSlotCount = String(slotCount);

  elementInstafeedVideoObserver?.disconnect();
  elementInstafeedVideoObserver = undefined;
  clearElementInstafeedRuntimeListeners();

  const videos = [...track.querySelectorAll('video')];
  const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const syncVideoPlayback = (video) => {
    const shouldPlay =
      video.dataset.meInstafeedVisible === 'true' &&
      document.visibilityState === 'visible' &&
      !reducedMotionQuery?.matches;

    if (shouldPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  if (videos.length && 'IntersectionObserver' in window) {
    elementInstafeedVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          video.dataset.meInstafeedVisible = String(entry.isIntersecting);
          syncVideoPlayback(video);
        });
      },
      {
        root: viewport,
        rootMargin: '0px 25%',
        threshold: 0.05,
      }
    );

    videos.forEach((video) => {
      video.addEventListener('loadeddata', () => syncVideoPlayback(video));
      elementInstafeedVideoObserver.observe(video);
    });
  } else {
    videos.forEach((video) => {
      video.dataset.meInstafeedVisible = 'true';
      syncVideoPlayback(video);
    });
  }

  const isTrackPausedByInteraction = () => {
    let isPausedByInteraction = false;
    try {
      isPausedByInteraction =
        Boolean(
          viewport?.matches(
            ':has(.me-social-scroll__item:focus-visible)'
          )
        ) ||
        Boolean(
          shouldPauseMeAutoScrollForHover() &&
            viewport?.matches(':has(.me-social-scroll__item:hover)')
        );
    } catch {
      isPausedByInteraction = false;
    }
    return isPausedByInteraction;
  };
  const baseDuration = Math.max(
    Number.parseFloat(
      getComputedStyle(section).getPropertyValue('--me-social-base-duration')
    ) ||
      Number.parseFloat(
        getComputedStyle(section).getPropertyValue('--me-social-duration')
      ) ||
      34,
    1
  );
  const duration = baseDuration * Math.max(slotCount / minimumSlotCount, 1);
  let trackOffset = 0;
  let lastTrackTimestamp;
  const getTrackLoopWidth = () => {
    const firstDuplicate = track.children[slotCount];
    const firstItemOffset = track.firstElementChild?.offsetLeft || 0;
    return firstDuplicate
      ? firstDuplicate.offsetLeft - firstItemOffset
      : track.scrollWidth / 2;
  };
  const requestTrackFrame = () => {
    if (
      elementInstafeedTrackAnimationFrame == null &&
      document.visibilityState === 'visible' &&
      !reducedMotionQuery?.matches
    ) {
      elementInstafeedTrackAnimationFrame = window.requestAnimationFrame(scrollTrack);
    }
  };
  const scrollTrack = (timestamp) => {
    elementInstafeedTrackAnimationFrame = undefined;
    if (!track.isConnected) return;

    const loopWidth = getTrackLoopWidth();
    const delta = lastTrackTimestamp == null
      ? 0
      : Math.min(timestamp - lastTrackTimestamp, 64);
    lastTrackTimestamp = timestamp;

    if (
      loopWidth > 0 &&
      document.visibilityState === 'visible' &&
      !reducedMotionQuery?.matches &&
      !isTrackPausedByInteraction()
    ) {
      trackOffset = (
        trackOffset + (loopWidth / duration) * (delta / 1000)
      ) % loopWidth;
      track.style.transform = `translate3d(${-trackOffset}px, 0, 0)`;
    }

    requestTrackFrame();
  };
  const syncRuntime = () => {
    videos.forEach(syncVideoPlayback);
    lastTrackTimestamp = undefined;

    if (
      document.visibilityState !== 'visible' ||
      reducedMotionQuery?.matches
    ) {
      window.cancelAnimationFrame(elementInstafeedTrackAnimationFrame);
      elementInstafeedTrackAnimationFrame = undefined;

      if (reducedMotionQuery?.matches) {
        trackOffset = 0;
        track.style.removeProperty('transform');
      }
      return;
    }

    requestTrackFrame();
  };

  elementInstafeedVisibilityHandler = syncRuntime;
  elementInstafeedPageShowHandler = syncRuntime;
  elementInstafeedReducedMotionQuery = reducedMotionQuery;
  document.addEventListener('visibilitychange', elementInstafeedVisibilityHandler);
  window.addEventListener('pageshow', elementInstafeedPageShowHandler);
  if (reducedMotionQuery?.addEventListener) {
    reducedMotionQuery.addEventListener('change', elementInstafeedVisibilityHandler);
  } else {
    reducedMotionQuery?.addListener?.(elementInstafeedVisibilityHandler);
  }

  syncRuntime();

  return true;
};

const initElementInstafeed = (event) => {
  const eventTarget = event?.target instanceof Element ? event.target : null;
  const section = eventTarget
    ? eventTarget.matches('.me-social-scroll')
      ? eventTarget
      : eventTarget.querySelector('.me-social-scroll')
    : document.querySelector('.me-social-scroll');
  const mount = section?.querySelector('[data-me-instafeed-mount]');

  if (!section || !mount) return;

  elementInstafeedObserver?.disconnect();
  elementInstafeedContentObserver?.disconnect();
  elementInstafeedVideoObserver?.disconnect();
  elementInstafeedVideoObserver = undefined;
  elementInstafeedSectionObserver?.disconnect();
  elementInstafeedSectionObserver = undefined;
  if (elementInstafeedRecoveryHandler) {
    window.removeEventListener('load', elementInstafeedRecoveryHandler);
    window.removeEventListener('pageshow', elementInstafeedRecoveryHandler);
    window.removeEventListener('online', elementInstafeedRecoveryHandler);
  }
  elementInstafeedRecoveryHandler = undefined;
  window.clearInterval(elementInstafeedRetryTimer);
  window.clearTimeout(elementInstafeedSyncTimer);
  clearElementInstafeedRuntimeListeners();
  elementInstafeedObservedFeed = undefined;
  elementInstafeedCandidateFingerprint = '';
  elementInstafeedCandidatePasses = 0;
  elementInstafeedCandidateSince = 0;
  delete section.dataset.meInstafeedReady;

  let retryCount = 0;
  let lastHydratedFingerprint = '';
  let lastHydratedLiveMediaCount = 0;
  const track = section.querySelector('[data-me-instafeed-track]');

  const getFeed = () => {
    const feed = mount.matches('#insta-feed, #instafeed')
      ? mount
      : mount.querySelector('#insta-feed, #instafeed') || mount;

    if (elementInstafeedObservedFeed !== feed) {
      elementInstafeedContentObserver?.disconnect();
      elementInstafeedContentObserver = new MutationObserver(() => scheduleSync());
      elementInstafeedContentObserver.observe(feed, {
        attributes: true,
        attributeFilter: [
          'href',
          'src',
          'srcset',
          'data-src',
          'data-lazy-src',
          'data-original',
          'data-original-src',
          'data-srcset',
          'data-media-id',
          'data-post-id',
          'data-instafeed-src',
          'data-instafeed-video-src',
          'data-video-src',
          'data-video-url',
          'style',
        ],
        childList: true,
        subtree: true,
      });
      elementInstafeedObservedFeed = feed;
    }

    primeElementInstafeedMedia(feed);
    return feed;
  };

  const syncFeed = (force = false) => {
    const feed = getFeed();
    if (!feed) return false;

    primeElementInstafeedMedia(feed);
    const snapshot = getElementInstafeedSnapshot(feed);
    if (!snapshot.records.length || snapshot.liveMediaCount < 1) return false;

    if (snapshot.fingerprint === elementInstafeedCandidateFingerprint) {
      elementInstafeedCandidatePasses += 1;
    } else {
      elementInstafeedCandidateFingerprint = snapshot.fingerprint;
      elementInstafeedCandidatePasses = 1;
      elementInstafeedCandidateSince = Date.now();
    }

    const isStable =
      elementInstafeedCandidatePasses >= 2 &&
      Date.now() - elementInstafeedCandidateSince >= 250;
    const hasNewMedia =
      snapshot.liveMediaCount > lastHydratedLiveMediaCount ||
      snapshot.fingerprint !== lastHydratedFingerprint;
    const shouldHydrate = hasNewMedia && (isStable || force);

    if (!shouldHydrate) return false;

    const hydrated = hydrateElementInstafeedTrack(section, snapshot, {
      finalize: false,
    });
    if (!hydrated) return false;

    lastHydratedFingerprint = snapshot.fingerprint;
    lastHydratedLiveMediaCount = snapshot.liveMediaCount;

    // The source feed stays mounted and observed so every post supplied by the
    // Instagram app can be added, including posts that arrive after the first
    // visible batch on a cold page load.
    elementInstafeedObserver?.disconnect();
    elementInstafeedObserver = undefined;
    mount.querySelectorAll('video').forEach((video) => video.pause());
    return false;
  };

  function scheduleSync() {
    window.clearTimeout(elementInstafeedSyncTimer);
    elementInstafeedSyncTimer = window.setTimeout(() => syncFeed(), 100);
  }

  scheduleSync();

  elementInstafeedRecoveryHandler = () => {
    const feed = getFeed();
    primeElementInstafeedMedia(feed);
    scheduleSync();
  };
  window.addEventListener('load', elementInstafeedRecoveryHandler, { once: true });
  window.addEventListener('pageshow', elementInstafeedRecoveryHandler);
  window.addEventListener('online', elementInstafeedRecoveryHandler);

  if ('IntersectionObserver' in window) {
    elementInstafeedSectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          elementInstafeedRecoveryHandler();
        }
      },
      { rootMargin: '800px 0px', threshold: 0.01 }
    );
    elementInstafeedSectionObserver.observe(section);
  }

  const main = document.querySelector('main');
  if (main) {
    elementInstafeedObserver = new MutationObserver(() => scheduleSync());
    elementInstafeedObserver.observe(main, { childList: true, subtree: true });
  }

  elementInstafeedRetryTimer = window.setInterval(() => {
    retryCount += 1;
    const forcePartialRefresh = retryCount >= 16;
    if (syncFeed(forcePartialRefresh) || retryCount >= 120) {
      window.clearInterval(elementInstafeedRetryTimer);
    }
  }, 500);
};

const unloadElementInstafeed = (event) => {
  const eventTarget = event?.target instanceof Element ? event.target : null;
  const section = eventTarget
    ? eventTarget.matches('.me-social-scroll')
      ? eventTarget
      : eventTarget.querySelector('.me-social-scroll')
    : document.querySelector('.me-social-scroll');

  if (eventTarget && !section) return;

  elementInstafeedObserver?.disconnect();
  elementInstafeedObserver = undefined;
  elementInstafeedContentObserver?.disconnect();
  elementInstafeedContentObserver = undefined;
  elementInstafeedObservedFeed = undefined;
  window.clearInterval(elementInstafeedRetryTimer);
  elementInstafeedRetryTimer = undefined;
  window.clearTimeout(elementInstafeedSyncTimer);
  elementInstafeedSyncTimer = undefined;
  elementInstafeedVideoObserver?.disconnect();
  elementInstafeedVideoObserver = undefined;
  elementInstafeedSectionObserver?.disconnect();
  elementInstafeedSectionObserver = undefined;
  if (elementInstafeedRecoveryHandler) {
    window.removeEventListener('load', elementInstafeedRecoveryHandler);
    window.removeEventListener('pageshow', elementInstafeedRecoveryHandler);
    window.removeEventListener('online', elementInstafeedRecoveryHandler);
  }
  elementInstafeedRecoveryHandler = undefined;
  clearElementInstafeedRuntimeListeners();
  elementInstafeedCandidateFingerprint = '';
  elementInstafeedCandidatePasses = 0;
  elementInstafeedCandidateSince = 0;
};

initElementAutoScroll();
initHomeSwimAutoScroll();
initProductCardTapReveal();
initElementCursorPrices();
initUntamedCarousel();
initAccountBackdrop();
initElementInstafeed();
document.addEventListener('shopify:section:load', initElementAutoScroll);
document.addEventListener('shopify:section:load', initHomeSwimAutoScroll);
document.addEventListener('shopify:section:load', initAccountBackdrop);
document.addEventListener('shopify:section:load', initElementCursorPrices);
document.addEventListener('shopify:section:load', initUntamedCarousel);
document.addEventListener('shopify:section:load', initElementInstafeed);
document.addEventListener('shopify:section:unload', unloadElementInstafeed);
